import React, { useMemo, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailIcon from '@mui/icons-material/Email';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SaveIcon from '@mui/icons-material/Save';
import {
  createEmailTemplate,
  EmailTemplate,
  updateEmailTemplate,
} from '../services/emailApi';
import EmailAiEditorDialog from './EmailAiEditorDialog';

interface EmailFormProps {
  onBack: () => void;
  onCreated?: (template: EmailTemplate) => void;
  /** When provided, the form switches to edit mode. */
  editTemplate?: EmailTemplate | null;
  onUpdated?: (template: EmailTemplate) => void;
}

// Default starter body. Mirrors the visual language of the production
// Huttons email templates in `backend/src/email_template/` — dark navy canvas,
// 620px centred card, orange (#ed8936) accent. Intentionally generic copy so
// users can replace the wording without touching the styling.
const SAMPLE_HTML = `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#031529;font-family:Arial,Helvetica,sans-serif;color:#e2e6ec;">
<tr>
<td align="center">

<table width="620" cellpadding="0" cellspacing="0" border="0" style="background:#041D40;">

  <!-- HEADER -->
  <tr>
    <td style="padding:35px 40px 25px 40px;border-bottom:1px solid rgba(237,137,54,0.18);">

      <div style="font-size:11px;letter-spacing:2px;color:#ed8936;text-transform:uppercase;margin-bottom:14px;">
        ★ A Note From Huttons ★
      </div>

      <div style="margin-bottom:4px;">
        <img src="https://firebasestorage.googleapis.com/v0/b/transformation-office.firebasestorage.app/o/huttons-logo%2FHuttons_white_fit.png?alt=media&token=ffa0eb31-e0cf-4c0a-aff4-368955cb9cb1"
          width="180" height="36" alt="Huttons"
          style="display:block;height:36px;width:auto;max-width:180px;border:0;"/>
      </div>

      <div style="font-size:42px;font-weight:bold;line-height:1.1;color:#ffffff;margin-bottom:8px;">
        Your <span style="color:#ed8936;">Headline</span> Here
      </div>

      <div style="font-size:15px;font-style:italic;color:#bbbbbb;margin-top:10px;">
        Replace this tagline with one short sentence that sets the scene.
      </div>

      <div style="font-size:11px;color:#9aa4b2;margin-top:8px;text-transform:uppercase;">
        Brought to you by the Projects &amp; Transformation Office
      </div>

    </td>
  </tr>

  <!-- ORANGE BANNER -->
  <tr>
    <td style="background:#ed8936;padding:15px 20px;text-align:center;color:#041D40;font-weight:bold;font-size:14px;">
      A short, punchy banner message goes here
    </td>
  </tr>

  <!-- BODY -->
  <tr>
    <td style="padding:40px;">

      <div style="font-size:16px;font-weight:bold;color:#ffffff;margin-bottom:12px;">
        Hi {{1}},
      </div>

      <div style="font-size:15px;line-height:1.7;color:#cfd6e1;margin-bottom:28px;">
        Replace this paragraph with your opening message. Use
        <strong style="color:#ffffff;">white text</strong> to draw attention to key phrases and
        <strong style="color:#ed8936;">orange text</strong> for the most important highlight.<br><br>
        Keep paragraphs short — most readers will skim on mobile first.
      </div>

      <!-- Highlight box -->
      <div style="background:#082a55;border:1px solid #ed8936;border-top:3px solid #ed8936;padding:20px;margin-bottom:30px;">
        <div style="font-size:18px;font-weight:bold;color:#ffffff;margin-bottom:5px;">Section heading</div>
        <div style="font-size:13px;color:#cfd6e1;line-height:1.7;margin-bottom:14px;">
          A short blurb explaining what this section is about.
        </div>
        <div style="margin-bottom:8px;color:#cfd6e1;">✦ &nbsp;First bullet — <strong style="color:#ffffff;">key benefit</strong></div>
        <div style="margin-bottom:8px;color:#cfd6e1;">✦ &nbsp;Second bullet — <strong style="color:#ffffff;">another benefit</strong></div>
        <div style="color:#cfd6e1;">✦ &nbsp;Third bullet — <strong style="color:#ffffff;">closing benefit</strong></div>
      </div>

      <!-- CTA -->
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto 32px auto;">
        <tr>
          <td align="center">
            <a href="https://htns.sg"
               style="background:#ed8936;color:#041D40;text-decoration:none;padding:16px 40px;font-weight:bold;font-size:15px;display:inline-block;border-radius:30px;letter-spacing:0.5px;">
              👉 &nbsp; Primary Call to Action
            </a>
          </td>
        </tr>
      </table>

      <!-- Quote -->
      <div style="margin-bottom:30px;padding:16px 20px;background:#082a55;border-left:3px solid #ed8936;">
        <div style="font-style:italic;font-size:15px;color:#e6ecf4;line-height:1.6;">"An optional pull-quote that reinforces the message — replace or delete as needed."</div>
        <div style="font-size:12px;color:#ed8936;margin-top:8px;">Attribution · Title</div>
      </div>

      <!-- Sign off -->
      <div style="font-size:15px;color:#cfd6e1;line-height:1.7;">
        Closing line goes here.<br><br>
        Cheers,<br>
        <img src="https://firebasestorage.googleapis.com/v0/b/super-picker-41006.firebasestorage.app/o/edm%2Fjy_linus_no_bg_small.png?alt=media&token=5b15af3b-0f82-48fb-89bd-d7b6888a7ec7"
          alt="JY &amp; Linus Low"
          style="display:block;max-width:200px;height:auto;border-radius:8px;margin-top:8px;margin-bottom:6px;" />
        <strong style="color:#ffffff;">JY &amp; Linus Low</strong><br>
        <span style="font-size:12px;color:#9aa4b2;text-transform:uppercase;">Projects &amp; Transformation Office · Huttons Asia</span>
      </div>

    </td>
  </tr>

  <!-- FOOTER -->
  <tr>
    <td style="background:#02101f;padding:25px 40px;text-align:center;border-top:1px solid rgba(237,137,54,0.12);">

      <div style="font-style:italic;font-size:18px;color:#ed8936;margin-bottom:5px;">
        JY &amp; Linus Low
      </div>

      <div style="font-size:11px;color:#9aa4b2;text-transform:uppercase;margin-bottom:15px;">
        Projects &amp; Transformation Office · Huttons Asia
      </div>

      <div style="font-size:11px;color:#6b7c93;line-height:1.6;">
        This communication is intended for Huttons agents and associates.<br>
        © 2026 Huttons Asia Pte Ltd. All rights reserved.
      </div>

    </td>
  </tr>

</table>

</td>
</tr>
</table>`;

/**
 * `{{1}}`, `{{2}}`, … are the only placeholders the sender fills in.
 * Empty `{{}}` and anything else (`{{ name }}`, `{{email}}`) are rejected:
 * Elastic Email runs `{{ }}` as JavaScript and then drops the message.
 */
function unsupportedPlaceholders(text: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  const re = /\{\{([^{}]*)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const inner = match[1] ?? '';
    if (/^\d+$/.test(inner)) continue;
    const token = match[0];
    if (seen.has(token)) continue;
    seen.add(token);
    found.push(token);
  }
  return found;
}

function PlaceholderList({ label, tokens }: { label: string; tokens: string[] }) {
  return (
    <Box sx={{ mt: 1.75 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
        {tokens.map((token) => (
          <Box
            key={token}
            component="code"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.16)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            {token}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function EmailForm({
  onBack,
  onCreated,
  editTemplate = null,
  onUpdated,
}: EmailFormProps) {
  const isEdit = !!editTemplate;

  const [name, setName] = useState(editTemplate?.name ?? '');
  const [subject, setSubject] = useState(editTemplate?.subject ?? '');
  const [htmlBody, setHtmlBody] = useState(editTemplate?.htmlBody ?? SAMPLE_HTML);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<EmailTemplate | null>(null);

  const [aiEditorOpen, setAiEditorOpen] = useState(false);

  // Jodit instance ref + memoized config. The config MUST be memoized:
  // jodit-react re-creates the editor whenever a new config object identity
  // is passed, which would lose cursor position on every render.
  const joditRef = useRef(null);
  const joditConfig = useMemo(
    () => ({
      readonly: false,
      height: 520,
      placeholder: 'Compose your email…',
      // Default Jodit toolbar exposes the full feature set (formatting, lists,
      // tables, images, links, source view, preview, fullscreen, etc.). We
      // intentionally don't override `buttons` so users get everything.
      toolbarAdaptive: false,
      // Keep paste behaviour predictable for email content.
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      // {{1}} placeholders should round-trip untouched.
      cleanHTML: { removeEmptyElements: false },
    }),
    [],
  );

  const subjectPlaceholders = useMemo(() => unsupportedPlaceholders(subject), [subject]);
  const bodyPlaceholders = useMemo(() => unsupportedPlaceholders(htmlBody), [htmlBody]);
  const hasUnsupportedPlaceholders =
    subjectPlaceholders.length > 0 || bodyPlaceholders.length > 0;

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!isEdit && name.trim().length === 0) return false;
    if (subject.trim().length === 0) return false;
    if (htmlBody.trim().length === 0) return false;
    if (hasUnsupportedPlaceholders) return false;
    return true;
  }, [name, subject, htmlBody, submitting, isEdit, hasUnsupportedPlaceholders]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      if (isEdit && editTemplate) {
        const response = await updateEmailTemplate(editTemplate.id, {
          subject: subject.trim(),
          htmlBody,
        });
        setSuccess(response);
        onUpdated?.(response);
      } else {
        const response = await createEmailTemplate({
          name: name.trim(),
          subject: subject.trim(),
          htmlBody,
        });
        setSuccess(response);
        onCreated?.(response);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isEdit
          ? 'Failed to update email template.'
          : 'Failed to create email template.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to templates
      </Button>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <EmailIcon sx={{ color: '#1976D2', fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {isEdit ? 'Edit email template' : 'Create email template'}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
        Compose the HTML body using the rich-text editor. Use the toolbar's preview button to see
        how it will render in an inbox before saving.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <TextField
                label="Template name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                helperText={
                  isEdit
                    ? 'Template name cannot be changed after creation.'
                    : 'Internal name used to identify this template.'
                }
                inputProps={{ maxLength: 255 }}
                disabled={isEdit}
                fullWidth
              />

              <TextField
                label="Subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ maxLength: 512 }}
                helperText={`${subject.length}/512 — supports {{1}}, {{2}} … placeholders.`}
                fullWidth
              />

              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    HTML body
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setAiEditorOpen(true)}
                    disabled={submitting}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#7B1FA2',
                      color: '#7B1FA2',
                      '&:hover': { borderColor: '#6A1B9A', bgcolor: '#F3E5F5' },
                    }}
                  >
                    Edit with AI
                  </Button>
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
                >
                  Use the toolbar to format your email. Use
                  <code style={{ marginLeft: 4 }}>{'{{1}}'}</code>,{' '}
                  <code>{'{{2}}'}</code>… for variables. Toggle <strong>Source</strong> in the
                  toolbar to hand-edit raw HTML.
                </Typography>
                <JoditEditor
                  ref={joditRef}
                  value={htmlBody}
                  config={joditConfig}
                  onBlur={(newContent) => setHtmlBody(newContent)}
                />
                {hasUnsupportedPlaceholders && (
                  <Box
                    role="alert"
                    sx={{
                      width: '100%',
                      mt: 2,
                      px: 3,
                      py: 2.5,
                      boxSizing: 'border-box',
                      borderRadius: 1,
                      bgcolor: '#B71C1C',
                      color: '#fff',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <ErrorOutlineIcon sx={{ fontSize: 42, mt: 0.25, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.3 }}>
                          This template cannot be saved
                        </Typography>
                        <Typography sx={{ fontSize: 16, lineHeight: 1.5, mt: 1 }}>
                          Placeholders must be numbers only, like {'{{1}}'} or {'{{2}}'}. Empty
                          braces and any other text inside {'{{ }}'} are unsupported. Fix every
                          item below before saving.
                        </Typography>
                        {subjectPlaceholders.length > 0 && (
                          <PlaceholderList label="Subject" tokens={subjectPlaceholders} />
                        )}
                        {bodyPlaceholders.length > 0 && (
                          <PlaceholderList label="HTML body" tokens={bodyPlaceholders} />
                        )}
                      </Box>
                    </Stack>
                  </Box>
                )}
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                >
                  {htmlBody.length.toLocaleString()} characters
                </Typography>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
              {success && (
                <Alert
                  severity="success"
                  action={
                    <Button color="inherit" size="small" onClick={onBack}>
                      Go to templates
                    </Button>
                  }
                >
                  Template <strong>{success.name}</strong> {isEdit ? 'updated' : 'created'}.
                </Alert>
              )}

              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button type="button" color="inherit" onClick={onBack} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                    ) : isEdit ? (
                      <SaveIcon />
                    ) : (
                      <AddIcon />
                    )
                  }
                  sx={{ bgcolor: '#1976D2', '&:hover': { bgcolor: '#115293' } }}
                >
                  {submitting
                    ? isEdit
                      ? 'Saving…'
                      : 'Creating…'
                    : isEdit
                    ? 'Save changes'
                    : 'Create'}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <EmailAiEditorDialog
        open={aiEditorOpen}
        subject={subject}
        currentHtml={htmlBody}
        onClose={() => setAiEditorOpen(false)}
        onApply={(html) => setHtmlBody(html)}
      />
    </Box>
  );
}
